import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import { api, LEAD_ACTIVITY_LIMIT } from '../../lib/api';
import { resolveDisplayName } from '../../lib/usersDirectory';
import { htmlToPlainText } from '../../lib/emailCompose';
import {
  FEED_FILTERS,
  FEED_PAGE_SIZE,
  FEED_SOURCE_BUFFER,
  buildLeadFeed,
  computeFeedHorizon,
  feedItemMatchesFilter,
  formatFeedFull,
  formatFeedRelative,
  isFeedItemAtOrAfter,
} from '../../lib/leadActivityFeed';

const {
  FiActivity, FiArrowRight, FiBell, FiCalendar, FiCheckCircle, FiChevronDown, FiChevronUp,
  FiFileText, FiGitMerge, FiMail, FiMessageSquare, FiPhone, FiRefreshCw, FiRepeat, FiTool, FiZap,
} = FiIcons;

/** Sources the page does not already hold; each is capped and ordered by `field` desc. */
const FEED_SOURCES = [
  { key: 'smsMessages', label: 'SMS', field: 'created_at', load: (id, n) => api.leadActivityFeed.smsMessages(id, n) },
  { key: 'emailLogs', label: 'email', field: 'created_at', load: (id, n) => api.leadActivityFeed.emailLogs(id, n) },
  { key: 'reminders', label: 'reminders', field: 'updated_at', load: (id, n) => api.leadActivityFeed.reminders(id, n) },
  { key: 'enrollments', label: 'sequences', field: 'enrolled_at', load: (id, n) => api.leadActivityFeed.enrollments(id, n) },
  { key: 'sessions', label: 'sessions', field: 'created_at', load: (id, n) => api.leadActivityFeed.workspaceSessions(id, n) },
];

const TAB_LABELS = {
  calls: 'Calls',
  sms: 'SMS',
  email: 'Email',
  notes: 'Notes',
  reminders: 'Reminders',
  sequences: 'Sequences',
  sessions: 'Sessions',
};

const KIND_STYLES = {
  call: { icon: FiPhone, className: 'bg-blue-100 text-blue-600' },
  ai_call: { icon: FiZap, className: 'bg-violet-100 text-violet-600' },
  sms: { icon: FiMessageSquare, className: 'bg-cyan-100 text-cyan-700' },
  email: { icon: FiMail, className: 'bg-indigo-100 text-indigo-600' },
  note: { icon: FiFileText, className: 'bg-yellow-100 text-yellow-700' },
  appointment: { icon: FiCalendar, className: 'bg-sky-100 text-sky-700' },
  reminder: { icon: FiBell, className: 'bg-orange-100 text-orange-700' },
  sequence: { icon: FiRepeat, className: 'bg-purple-100 text-purple-700' },
  session: { icon: FiTool, className: 'bg-gray-100 text-gray-600' },
};

function activityLogStyle(activityType) {
  switch (activityType) {
    case 'event_registration':
      return { icon: FiCalendar, className: 'bg-emerald-100 text-emerald-700' };
    case 'appointment_booked':
    case 'appointment_rescheduled':
    case 'appointment_cancelled':
      return { icon: FiCalendar, className: 'bg-sky-100 text-sky-700' };
    case 'merge':
    case 'csv_import_merge':
      return { icon: FiGitMerge, className: 'bg-amber-100 text-amber-700' };
    case 'manual_calls':
      return { icon: FiPhone, className: 'bg-blue-100 text-blue-600' };
    default:
      return { icon: FiCheckCircle, className: 'bg-blue-100 text-blue-600' };
  }
}

function itemStyle(item) {
  if (item.kind === 'activity') return activityLogStyle(item.activityType);
  if (item.kind === 'appointment' && item.cancelled) {
    return { icon: FiCalendar, className: 'bg-red-100 text-red-600' };
  }
  return KIND_STYLES[item.kind] || { icon: FiActivity, className: 'bg-gray-100 text-gray-600' };
}

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{children}</p>
  );
}

const FeedRow = memo(function FeedRow({ item, expanded, onToggle, onOpenTab, now }) {
  const { icon, className } = itemStyle(item);
  const hasExtra = Boolean(item.details?.length || item.body || item.transcript || item.tab);
  return (
    <li className="bg-white rounded-lg border border-gray-100 shadow-sm">
      <button
        type="button"
        onClick={() => onToggle(item.key)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg hover:bg-gray-50/80 transition-colors"
      >
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${className}`}>
          <SafeIcon icon={icon} className="w-3.5 h-3.5" />
        </span>
        <span className="flex-1 min-w-0 truncate text-sm">
          <span className="font-semibold text-gray-900">{item.title}</span>
          {item.actor && <span className="text-gray-500"> by {item.actor}</span>}
          {item.subtitle && <span className="text-gray-700"> — {item.subtitle}</span>}
          {item.preview && <span className="text-gray-500"> · {item.preview}</span>}
        </span>
        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0" title={formatFeedFull(item.at)}>
          {formatFeedRelative(item.at, now)}
        </span>
        <SafeIcon
          icon={expanded ? FiChevronUp : FiChevronDown}
          className={`w-4 h-4 shrink-0 ${hasExtra ? 'text-gray-400' : 'text-gray-200'}`}
        />
      </button>
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 sm:pl-[3.25rem] space-y-3 text-sm">
          {item.details?.length > 0 && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {item.details.map((d) => (
                <div key={d.label} className="flex gap-2 min-w-0">
                  <dt className="text-gray-500 shrink-0">{d.label}</dt>
                  <dd className="text-gray-800 break-words min-w-0">{d.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {item.body && (
            <div>
              {item.bodyLabel && <SectionLabel>{item.bodyLabel}</SectionLabel>}
              <p className="whitespace-pre-wrap break-words text-gray-700">{item.body}</p>
            </div>
          )}
          {item.transcript && (
            <div>
              <SectionLabel>Transcript</SectionLabel>
              <p className="whitespace-pre-wrap break-words text-gray-600 bg-gray-50 rounded-md p-3 border border-gray-100 max-h-64 overflow-y-auto">
                {item.transcript}
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-400">{formatFeedFull(item.at)}</span>
            {item.tab && onOpenTab && (
              <button
                type="button"
                onClick={() => onOpenTab(item.tab, item.tabOptions)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                View in {TAB_LABELS[item.tab] || item.tab}
                <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
});

function FeedSkeleton() {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <li key={i} className="bg-white rounded-lg border border-gray-100 shadow-sm px-3 py-2.5 flex items-center gap-3 animate-pulse">
          <span className="w-7 h-7 rounded-full bg-gray-100 shrink-0" />
          <span className="h-3 rounded bg-gray-100 flex-1" style={{ maxWidth: `${80 - i * 7}%` }} />
          <span className="h-3 w-10 rounded bg-gray-100 shrink-0 ml-auto" />
        </li>
      ))}
    </ul>
  );
}

export default function LeadActivityFeed({
  lead,
  appointments,
  thoughtlyCalls,
  thoughtlyAgents,
  refreshToken,
  lastRefreshed,
  refreshing,
  onRefresh,
  onOpenTab,
  onCallLead,
  hasPhone,
}) {
  const leadId = lead?.id;
  const [filter, setFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(FEED_PAGE_SIZE);
  const [fetchLimit, setFetchLimit] = useState(FEED_PAGE_SIZE + FEED_SOURCE_BUFFER);
  const [sourceData, setSourceData] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const requestRef = useRef(0);

  useEffect(() => {
    setSourceData(null);
    setVisibleCount(FEED_PAGE_SIZE);
    setFetchLimit(FEED_PAGE_SIZE + FEED_SOURCE_BUFFER);
    setExpanded(new Set());
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    const requestId = ++requestRef.current;
    setFetching(true);
    Promise.allSettled(FEED_SOURCES.map((s) => s.load(leadId, fetchLimit))).then((results) => {
      if (requestId !== requestRef.current) return;
      const rows = {};
      const failed = [];
      results.forEach((result, i) => {
        const source = FEED_SOURCES[i];
        if (result.status === 'fulfilled') {
          rows[source.key] = result.value ?? [];
        } else {
          rows[source.key] = [];
          failed.push(source.label);
          console.error(`[lead-activity-feed] ${source.key}`, result.reason);
        }
      });
      setSourceData({ limit: fetchLimit, rows, failed });
      setFetching(false);
    });
  }, [leadId, fetchLimit, refreshToken]);

  const items = useMemo(() => {
    if (!sourceData) return [];
    return buildLeadFeed({
      lead,
      appointments,
      thoughtlyCalls,
      thoughtlyAgents,
      smsMessages: sourceData.rows.smsMessages,
      emailLogs: sourceData.rows.emailLogs,
      reminders: sourceData.rows.reminders,
      enrollments: sourceData.rows.enrollments,
      sessions: sourceData.rows.sessions,
      resolveName: resolveDisplayName,
      toPlainText: htmlToPlainText,
    });
  }, [sourceData, lead, appointments, thoughtlyCalls, thoughtlyAgents]);

  const { horizon, canFetchMore } = useMemo(() => {
    if (!sourceData) return { horizon: null, canFetchMore: false };
    const activity = lead?.activity ?? [];
    const pageHorizon = computeFeedHorizon([
      { rows: lead?.callLogs ?? [], limit: LEAD_ACTIVITY_LIMIT, field: 'called_at' },
      { rows: activity.filter((a) => a.type === 'sms'), limit: LEAD_ACTIVITY_LIMIT, field: 'created_at' },
      { rows: activity.filter((a) => a.type === 'activity'), limit: LEAD_ACTIVITY_LIMIT, field: 'created_at' },
    ]);
    const fetchedHorizon = computeFeedHorizon(
      FEED_SOURCES.map((s) => ({ rows: sourceData.rows[s.key], limit: sourceData.limit, field: s.field })),
    );
    const combined = [pageHorizon, fetchedHorizon].filter((h) => h != null);
    return {
      horizon: combined.length ? Math.max(...combined) : null,
      // Fetching deeper only helps while our own sources, not the page's fixed caps, set the cut-off.
      canFetchMore: fetchedHorizon != null && (pageHorizon == null || fetchedHorizon > pageHorizon),
    };
  }, [sourceData, lead?.activity, lead?.callLogs]);

  const filtered = useMemo(
    () => items.filter((i) => feedItemMatchesFilter(i, filter) && isFeedItemAtOrAfter(i, horizon)),
    [items, filter, horizon],
  );
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount || canFetchMore;

  const toggle = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const changeFilter = (id) => {
    setFilter(id);
    setVisibleCount(FEED_PAGE_SIZE);
  };

  const loadMore = () => {
    const next = visibleCount + FEED_PAGE_SIZE;
    setVisibleCount(next);
    if (filtered.length < next && canFetchMore) {
      setFetchLimit((f) => Math.max(f, next + FEED_SOURCE_BUFFER));
    }
  };

  const now = Date.now();
  const filterLabel = FEED_FILTERS.find((f) => f.id === filter)?.label ?? '';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5 flex-1" role="tablist" aria-label="Filter activity">
          {FEED_FILTERS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={filter === id}
              onClick={() => changeFilter(id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === id
                  ? 'bg-primary text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {lastRefreshed && (
            <span className="text-[11px] text-gray-400 whitespace-nowrap">
              Updated {formatDistanceToNow(lastRefreshed, { addSuffix: true })}
            </span>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors"
            title="Refresh activity"
          >
            <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-primary' : ''}`} />
          </button>
        </div>
      </div>

      {sourceData?.failed.length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
          Couldn&apos;t load {sourceData.failed.join(', ')} activity. Other activity is shown below.
        </p>
      )}

      {!sourceData ? (
        <FeedSkeleton />
      ) : visible.length === 0 ? (
        filter === 'all' ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <SafeIcon icon={FiActivity} className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No activity yet</p>
            <p className="text-xs text-gray-400 mt-1">Log a call or send an SMS to start tracking activity.</p>
            {onCallLead && (
              <Button size="sm" className="mt-4" onClick={onCallLead} disabled={!hasPhone}>
                <SafeIcon icon={FiPhone} className="mr-2" />
                Call Lead
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-sm font-medium text-gray-700">No {filterLabel.toLowerCase()} activity</p>
            {hasMore && (
              <Button size="sm" variant="outline" className="mt-3" onClick={loadMore} disabled={fetching}>
                {fetching ? <><Spinner className="mr-2" />Loading…</> : 'Search older activity'}
              </Button>
            )}
          </div>
        )
      ) : (
        <>
          <ul className="space-y-2">
            {visible.map((item) => (
              <FeedRow
                key={item.key}
                item={item}
                expanded={expanded.has(item.key)}
                onToggle={toggle}
                onOpenTab={onOpenTab}
                now={now}
              />
            ))}
          </ul>
          {hasMore && (
            <div className="flex justify-center pt-1">
              <Button size="sm" variant="outline" onClick={loadMore} disabled={fetching}>
                {fetching ? <><Spinner className="mr-2" />Loading…</> : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
